import { useState } from "react";
import { CATEGORIES } from "@/mocks/gossip";
import SectionHeading from "./SectionHeading";

const FORM_URL = "https://readdy.ai/api/form/daenr4htto0ceqqnh0s0";

type Status = { type: "idle" | "success" | "error"; msg: string };

export default function Subscribe() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "success", msg: "订阅成功，我们会在更新时第一时间通知你。" });
      form.reset();
      return;
    }

    const email = String(fd.get("email") || "").trim();
    if (!email) {
      setStatus({ type: "error", msg: "请填写一个有效的邮箱地址。" });
      return;
    }

    setSending(true);
    setStatus({ type: "idle", msg: "" });
    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => {
        if (key !== "website_alt" && value) body.append(key, String(value));
      });
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (responseText && !response.ok ? responseText : "");
      const isSpam = /spam|form data is spam/i.test(serverMsg);
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus({ type: "success", msg: "订阅成功，我们会在该分类更新时第一时间通知你。" });
        form.reset();
      } else {
        setStatus({
          type: "error",
          msg: serverMsg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setStatus({ type: "error", msg: "网络出了点问题，请稍后再试。" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-background-100 py-16 md:py-20">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[7fr_5fr] lg:gap-12">
        <div>
          <SectionHeading
            eyebrow="追更订阅"
            title="订阅你关注的瓜田，更新不错过"
            desc="留下邮箱并选择常看的分类，每次该分类有新的盘点或合集时间线更新，我们都会第一时间把入口推给你。免费订阅，绝不打扰。"
          />

          <form
            id="subscribeForm"
            data-readdy-form="daenr4htto0ceqqnh0s0"
            onSubmit={handleSubmit}
            noValidate
            className="mt-8 space-y-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="relative flex-1">
                <i className="ri-mail-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="你的邮箱地址"
                  className="w-full rounded-2xl border border-background-200 bg-background-50 py-3.5 pl-12 pr-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                />
              </label>
              <button
                type="submit"
                disabled={sending}
                className="flex shrink-0 items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 px-7 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin" />
                    提交中…
                  </>
                ) : (
                  <>
                    立即追更
                    <i className="ri-send-plane-line ml-2" />
                  </>
                )}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-foreground-500">常看分类：</span>
              {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                <label
                  key={c.id}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors has-[:checked]:border-primary-300 has-[:checked]:bg-primary-100 has-[:checked]:text-primary-700"
                >
                  <input type="checkbox" name="categories" value={c.name} className="sr-only" />
                  {c.name}
                </label>
              ))}
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            <p
              className="min-h-[20px] text-sm font-medium"
              aria-live="polite"
              style={{ color: status.type === "error" ? "oklch(var(--primary-700))" : "oklch(var(--secondary-700))" }}
            >
              {status.msg}
            </p>
            <p className="text-xs text-foreground-400">
              点击订阅即表示你同意接收分类更新邮件。你可以在任何时候通过邮件底部的链接取消订阅。
            </p>
          </form>
        </div>

        <aside className="flex flex-col justify-between gap-6 rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
          <div className="space-y-6">
            <h3 className="font-heading text-xl font-bold text-foreground-950">线索与合作</h3>
            <p className="text-sm leading-relaxed text-foreground-600">
              你有独家线索、更正建议，或想谈盘点内容合作？欢迎随时来信，编辑组会认真对待每一条反馈，并在核对后把它排进合适的时间线。
            </p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-800">
                  <i className="ri-mail-open-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-foreground-400">事件线索 / 更正建议</p>
                  <p className="font-medium text-foreground-900">tipline@jingri-guabao.example</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-800">
                  <i className="ri-community-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-foreground-400">内容 / 商务合作</p>
                  <p className="font-medium text-foreground-900">partner@jingri-guabao.example</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                  <i className="ri-timer-flash-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-foreground-400">更新时间</p>
                  <p className="font-medium text-foreground-900">全天候线索追踪 · 事件完结即归档</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="rounded-xl bg-secondary-100/70 p-4 text-xs leading-relaxed text-secondary-900">
            <i className="ri-information-line mr-1" />
            我们只整理公开、可回溯的信息，涉及事实的内容会等待官方回应后再更新。
          </div>
        </aside>
      </div>
    </section>
  );
}